import {Form, Formik, FormikHelpers} from "formik";
import Layout from "../../components/Layout";
import {
    capitalize,
    DialogActions,
    DialogContent,
    Paper,
    Typography,
} from "@mui/material";
import * as yup from "yup";
import React, {useCallback} from "react";
import {useNavigate} from "react-router";
import SubmitButton from "../../components/SubmitButton";
import {useTranslation} from "react-i18next";
import {useCreateMeineAbwesenheit} from "../../client/hooks";
import {useMutation, useQueryClient} from "react-query";
import {MyAbwesenheitDaten} from "../../api";
import AbwesenheitenFieldsContent from "../../components/abwesenheiten/AbwesenheitenFieldsContent";
import ErrorAlert from "../../components/ErrorAlert";
import {notPermitted} from "../../errors";
import Guard from "../../components/Guard";
import ShowFormikErrors from "../../components/allgemein/ShowFormikErrors";

export default function ErstelleMeineAbwesenheit() {
    const {t} = useTranslation("customer");
    const {mutateAsync} = useSaveAbwesenheit();
    let schema = yup.object().shape({
        start: yup
            .date()
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        ende: yup
            .date()
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        bemerkung: yup.string(),
        type: yup.string(),
        brzKey: yup
            .string()
            .nullable() // Erlaubt null, da das Feld optional ist
            .when("type", {
                is: "Arbeitszeitkonto",
                then: (schema) => schema.notRequired(),
                otherwise: (schema) =>
                    schema.required("BRZ-Schlüssel ist erforderlich!"),
            }),
    });
    const navigate = useNavigate();

    const onSubmit = useCallback(
        async (
            values: {
                start: string;
                ende: string;
                bemerkung: string;
                type: string;
                brzKey: string;
            },
            formikHelpers: FormikHelpers<{
                start: string;
                ende: string;
                bemerkung: string;
                type: string;
                brzKey: string;
            }>
        ) => {
            const {setSubmitting, setStatus} = formikHelpers;
            if (values.start !== "" && values.ende !== "") {
                await mutateAsync({
                    start: values.start,
                    ende: values.ende,
                    type: values.type,
                    bemerkung: values.bemerkung,
                    ...(values.brzKey !== "" && {brzKey: values.brzKey}),
                });

                navigate(`/meine-abwesenheit`);
            }
            setStatus(undefined);
            try {
            } catch (error: any) {
                setStatus(error.message);
            } finally {
                setSubmitting(false);
            }
        },
        [navigate, mutateAsync]
    );
    return (
        <Layout title={"Abwesenheit anlegen"} onBackClick={() => navigate(-1)}>
            <Guard
                permission={"meine:abwesenheit:create"}
                fallback={<ErrorAlert error={notPermitted()}/>}
            >
                <Paper
                    elevation={1}
                    sx={{marginTop: 2, padding: 3, marginLeft: 2, marginRight: 2}}
                >
                    <Formik
                        initialValues={{
                            start: "",
                            ende: "",
                            bemerkung: "",
                            type: "",
                            brzKey: "",
                        }}
                        validationSchema={schema}
                        onSubmit={onSubmit}
                    >
                        {({
                              isSubmitting,
                              isValidating,
                              isValid,
                              dirty,
                              errors,
                              validateForm,
                          }) => (
                            <>
                                <Typography variant="h5">Abwesenheit anlegen</Typography>
                                <DialogContent>
                                    <Form id="new-abwesenheit">
                                        <AbwesenheitenFieldsContent
                                            isVerwaltung={false}
                                            isNew={true}
                                        />
                                    </Form>
                                </DialogContent>
                                <DialogActions>
                                    <ShowFormikErrors errors={errors}>
                                        <SubmitButton
                                            form="new-abwesenheit"
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            loading={isSubmitting}
                                            disabled={isValidating || !isValid || !dirty}
                                            onMouseEnter={() => validateForm()}
                                        >
                                            {capitalize(t("save"))}
                                        </SubmitButton>
                                    </ShowFormikErrors>
                                </DialogActions>
                            </>
                        )}
                    </Formik>
                </Paper>
            </Guard>
        </Layout>
    );
}

function useSaveAbwesenheit() {
    const createAbwesenheit = useCreateMeineAbwesenheit();
    const queryClient = useQueryClient();

    return useMutation(
        (input: MyAbwesenheitDaten) => {
            return createAbwesenheit(input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["abwesenheiten"]);
            },
        }
    );
}
